import { CheckCircle, ChevronDown, ChevronRight, XCircle } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import type { TestResult, TestSummary } from '../execution-context'
import { SectionLabel } from './section-label'

interface RawResultsListProps {
  results: TestResult[]
  summary: TestSummary
}

/**
 * Results for raw test scripts (testType 'raw'): a collapsible row per
 * script with the script text and its traceback. The raw runner keeps its
 * legacy single-file shape (spec §1), so cases report a plain error string.
 */
export function RawResultsList({ results, summary }: RawResultsListProps) {
  const allPassed = summary.failed === 0

  return (
    <div className="h-full space-y-4 overflow-auto p-4">
      {/* Summary header */}
      <div className="flex items-center gap-3">
        {allPassed ? (
          <CheckCircle className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
        <h2
          className={cn(
            'text-lg font-semibold',
            allPassed ? 'text-green-500' : 'text-red-500',
          )}
        >
          {allPassed ? 'Accepted' : 'Failed'}
        </h2>
        <span className="text-muted-foreground text-sm">
          {summary.passed}/{summary.total} tests passed
        </span>
      </div>

      <div className="space-y-2">
        {results.map((result) => (
          <RawCaseRow key={`raw-${result.index}`} result={result} />
        ))}
      </div>
    </div>
  )
}

function RawCaseRow({ result }: { result: TestResult }) {
  const [isExpanded, setIsExpanded] = useState(!result.passed) // Auto-expand failed tests

  return (
    <div
      className={cn(
        'rounded-lg border',
        result.passed
          ? 'border-green-500/30 bg-green-500/5'
          : 'border-red-500/30 bg-red-500/5',
      )}
    >
      {/* Header - clickable to expand/collapse */}
      <button
        type="button"
        className="flex w-full items-center gap-2 p-3 text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        {isExpanded ? (
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        ) : (
          <ChevronRight className="text-muted-foreground h-4 w-4" />
        )}
        {result.passed ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500" />
        )}
        <span className="font-medium text-foreground">
          Test Case {result.index + 1}
        </span>
      </button>

      {/* Expanded details: script + error traceback */}
      {isExpanded && (
        <div className="space-y-3 border-t border-inherit p-3 pt-2">
          {result.script && (
            <div className="space-y-1">
              <SectionLabel>Test Script</SectionLabel>
              <pre className="bg-muted/50 overflow-x-auto rounded p-2 font-mono text-sm text-foreground whitespace-pre-wrap">
                {result.script}
              </pre>
            </div>
          )}
          {!result.passed && result.error && (
            <div className="space-y-1">
              <SectionLabel>Error</SectionLabel>
              <pre className="bg-muted/50 overflow-x-auto rounded p-2 font-mono text-sm whitespace-pre-wrap text-red-700 dark:text-red-300">
                {result.error}
              </pre>
            </div>
          )}
          {result.passed && (
            <p className="text-sm text-green-500">Test passed</p>
          )}
        </div>
      )}
    </div>
  )
}
